import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import HackCard from '@/components/HackCard';
import ProductCard from '@/components/ProductCard';
import ProductDetailDialog from '@/components/ProductDetailDialog';
import SmartImage from '@/components/SmartImage';
import WeatherWidget from '@/components/WeatherWidget';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, ArrowRight, Leaf, Star } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { PromoBadge } from '@/components/PromoBanner';

const HeroTip = ({ tip, loading }) => {
  if (loading) {
    return (
      <Card className="rounded-2xl bg-card border-border/70 overflow-hidden">
        <div className="grid md:grid-cols-2">
          <Skeleton className="h-72 md:h-full" />
          <div className="p-7 space-y-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
      </Card>
    );
  }
  if (!tip) return null;
  return (
    <Card
      className="rounded-2xl border-border/70 bg-card shadow-[0_10px_30px_rgba(31,26,23,0.06)] overflow-hidden"
      data-testid="daily-glow-tip-card"
    >
      <div className="grid md:grid-cols-2">
        <div className="relative h-64 md:h-auto">
          <img src={tip.image_url} alt={tip.title} className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=900&q=80'; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-card/40" />
        </div>
        <div className="p-6 sm:p-8 flex flex-col">
          <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-rose-gold">
            <Sparkles className="h-3.5 w-3.5" /> Daily Glow Tip
          </span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold leading-tight text-ink">
            {tip.title}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-foreground/85">{tip.body}</p>
          <div className="mt-5 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground border border-border rounded-full px-2 py-0.5">
              {tip.category}
            </span>
          </div>
          <div className="mt-auto pt-6 flex flex-wrap gap-3">
            <Button asChild className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90" data-testid="daily-glow-tip-cta-button">
              <Link to="/diy">Try a DIY Hack <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-xl">
              <Link to="/products">Browse Curator</Link>
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default function HomePage() {
  const { user, promo } = useAuth();
  const [tip, setTip] = useState(null);
  const [hacks, setHacks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openProduct, setOpenProduct] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const skinFilter = user?.skin_type ? `?skin_type=${user.skin_type}` : '';
        const [tipRes, hacksRes, productsRes] = await Promise.all([
          api.get('/tips/today'),
          api.get(`/hacks${skinFilter}`),
          api.get(`/products${skinFilter}`),
        ]);
        if (cancelled) return;
        setTip(tipRes.data);
        setHacks(hacksRes.data.slice(0, 6));
        setProducts(productsRes.data.slice(0, 6));
      } catch (e) {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user?.skin_type]);

  return (
    <div data-testid="home-page">
      <section className="hero-gradient noise-bg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 md:pt-16 md:pb-16">
          <div className="max-w-2xl mb-10">
            <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold">
              {user?.skin_type ? `Curated for ${user.skin_type} skin` : 'A daily ritual journal'}
            </span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-ink tracking-[-0.02em]">
              Your quiet-luxury<br /> beauty companion.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Discover thoughtfully curated DIY rituals from your kitchen and a smart edit of products
              — from <em className="text-foreground not-italic">Essential Picks</em> to{' '}
              <em className="text-foreground not-italic">Premium Luxe</em>. Built for women who care
              about their skin and their time.
            </p>
            {promo?.active && (
              <div className="mt-5" data-testid="home-promo-callout">
                <PromoBadge />
                <p className="mt-2 text-sm text-foreground/80 max-w-lg">
                  Try Ask AI, Photo Styling and our personalized Regimen — <strong>free for everyone</strong> during our launch celebration.
                </p>
              </div>
            )}
          </div>
          <HeroTip tip={tip} loading={loading} />
        </div>
      </section>

      {/* Weather-aware skin advice */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <WeatherWidget region={user?.region} />
      </section>

      {/* Featured DIY hacks */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="home-featured-hacks">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-rose-gold">
              <Leaf className="h-3.5 w-3.5" /> The DIY Beauty Lab
            </span>
            <h2 className="mt-2 font-display text-3xl font-semibold text-ink">
              Home-made hacks, time-tested.
            </h2>
            <p className="mt-1 text-sm text-muted-foreground max-w-md">
              Honey, turmeric, oats, aloe — simple natural ingredients with steps and benefits laid bare.
            </p>
          </div>
          <Button asChild variant="ghost" className="rounded-xl hidden sm:inline-flex">
            <Link to="/diy">View all <ArrowRight className="h-4 w-4 ml-1" /></Link>
          </Button>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-80 rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {hacks.map((h) => (
              <HackCard key={h.id} hack={h} />
            ))}
          </div>
        )}
        <div className="mt-6 text-center sm:hidden">
          <Button asChild variant="outline" className="rounded-xl">
            <Link to="/diy">Explore all hacks</Link>
          </Button>
        </div>
      </section>

      {/* Featured products */}
      <section className="bg-cream-paper border-y border-border/60" data-testid="home-featured-products">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="flex items-end justify-between mb-6">
            <div>
              <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-rose-gold">
                <Star className="h-3.5 w-3.5" /> Smart Product Curator
              </span>
              <h2 className="mt-2 font-display text-3xl font-semibold text-ink">
                Two tiers, one wishlist.
              </h2>
              <p className="mt-1 text-sm text-muted-foreground max-w-md">
                Reliable Essential Picks beside Premium Luxe — you choose where to splurge and where to save.
              </p>
            </div>
            <Button asChild variant="ghost" className="rounded-xl hidden sm:inline-flex">
              <Link to="/products">View curator <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-96 rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} onOpen={setOpenProduct} />
              ))}
            </div>
          )}
          <div className="mt-6 text-center sm:hidden">
            <Button asChild variant="outline" className="rounded-xl">
              <Link to="/products">Open the curator</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* AI Styling promo */}
      {user && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <Card className="rounded-2xl border-border/70 bg-card overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="relative bg-cream-paper hidden md:block">
                <SmartImage
                  src="https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=1200&q=80"
                  alt="AI styling"
                  fallbackKind="hack"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
              <div className="p-8 sm:p-10 flex flex-col">
                <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold">New: AI Beauty Styling</span>
                <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
                  Upload a selfie, get a stylist report.
                </h2>
                <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
                  Powered by Gemini 2.5. We analyse face shape, undertone, eye, hair and lip features, then suggest
                  makeup looks, hair styles, jewelry, perfume notes and curated products you can actually buy where you live.
                </p>
                <div className="mt-5 flex gap-3">
                  <Button asChild className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/styling">Try AI Styling</Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-xl">
                    <Link to="/next-steps">What to try next</Link>
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </section>
      )}

      {/* CTA */}
      {!user && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <Card className="rounded-2xl border-border/70 bg-card overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="p-8 sm:p-10">
                <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold">Get personal</span>
                <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
                  A beauty profile, made for your skin.
                </h2>
                <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
                  Tell us your skin type and concerns. We’ll quietly tailor every hack and product
                  recommendation, and keep your favorites in one place.
                </p>
                <div className="mt-5 flex gap-3">
                  <Button asChild className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/signup">Create my profile</Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-xl">
                    <Link to="/login">Sign in</Link>
                  </Button>
                </div>
              </div>
              <div className="hidden md:block bg-cream-paper relative">
                <img
                  src="https://images.unsplash.com/photo-1601049541289-9b1b7bbbfe19?w=900&q=80"
                  alt="Skincare routine"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
            </div>
          </Card>
        </section>
      )}

      <ProductDetailDialog
        product={openProduct}
        open={!!openProduct}
        onOpenChange={(o) => !o && setOpenProduct(null)}
      />
    </div>
  );
}
